import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appeler, cleNeuve } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Carte, Erreur } from '../../composants/communs.tsx';
import type { ReponseFournisseurs } from '../fournisseurs/types.ts';
import { LignesArticles, entierOuZero, entierPositif, type LigneSaisie } from './LignesArticles.tsx';
import { aujourdhui } from './types.ts';

/**
 * Saisie d'une réception fournisseur — RG-049.
 *
 * Identifiant et clé d'idempotence sont fixés À L'OUVERTURE : un double clic,
 * ou un envoi rejoué après une coupure, ne crée pas deux réceptions (RG-008).
 */
export function FormulaireReception({ onFerme }: { onFerme: () => void }): ReactNode {
  const { boutiqueId, boutiques, peut } = useSession();
  const client = useQueryClient();

  const [identite] = useState(() => ({ id: cleNeuve(), cle: cleNeuve() }));
  const [boutique, setBoutique] = useState<string>(boutiqueId ?? boutiques[0]?.id ?? '');
  const [fournisseur, setFournisseur] = useState('');
  // Masque les suggestions juste après en avoir choisi une : sans ça, la
  // liste reste ouverte en affichant le seul fournisseur qui correspond
  // déjà à ce qui vient d'être saisi.
  const [suggestionsMasquees, setSuggestionsMasquees] = useState(false);

  // Suggestions de fournisseurs déjà connus — Q-021 : texte libre, mais avec
  // suggestions, pour éviter qu'« Ets Koudjo » et « Etablissement Koudjo »
  // désignent le même fournisseur sans que rien ne les rapproche.
  const { data: fournisseursTrouves } = useQuery({
    queryKey: ['fournisseurs-suggestion', fournisseur],
    enabled: !suggestionsMasquees && fournisseur.trim().length >= 2,
    queryFn: () =>
      appeler<ReponseFournisseurs>(`/fournisseurs?q=${encodeURIComponent(fournisseur.trim())}`),
    // L'absence de `fournisseur.lire` ne doit pas bloquer la saisie : la
    // requête échoue en silence (elle n'est jamais affichée), la liste de
    // suggestions reste simplement vide et le champ libre demeure utilisable.
    select: (reponse) => reponse.donnees.slice(0, 6),
  });
  const [reference, setReference] = useState('');
  const [date, setDate] = useState(aujourdhui());
  const [frais, setFrais] = useState('');
  const [lignes, setLignes] = useState<LigneSaisie[]>([]);
  const avecCout = peut('reception.cout.saisir');

  const lignesValides = lignes.every(
    (ligne) =>
      entierPositif(ligne.quantite) !== null && (!avecCout || entierOuZero(ligne.cout) !== null),
  );
  const complet =
    boutique !== '' &&
    fournisseur.trim() !== '' &&
    lignes.length > 0 &&
    lignesValides &&
    (frais === '' || entierOuZero(frais) !== null);

  const envoi = useMutation({
    mutationFn: () =>
      appeler<{ numero: string }>('/receptions', {
        methode: 'POST',
        cleIdempotence: identite.cle,
        corps: {
          id: identite.id,
          boutique_id: boutique,
          fournisseur: fournisseur.trim(),
          reference_fournisseur: reference.trim() === '' ? null : reference.trim(),
          date_reception: date,
          ...(frais === '' ? {} : { frais_accessoires: Number(frais) }),
          lignes: lignes.map((ligne) => ({
            produit_id: ligne.produitId,
            quantite: Number(ligne.quantite),
            cout_unitaire: avecCout ? Number(ligne.cout) : 0,
          })),
        },
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['receptions'] });
      void client.invalidateQueries({ queryKey: ['produits'] });
      onFerme();
    },
  });

  return (
    <Carte
      titre="Nouvelle entrée en stock"
      actions={
        <button type="button" className="bouton--discret" onClick={onFerme}>
          Annuler
        </button>
      }
    >
      <div className="champs">
        <div className="champ">
          <label htmlFor="reception-boutique">Boutique qui reçoit</label>
          <select
            id="reception-boutique"
            value={boutique}
            onChange={(evenement) => {
              setBoutique(evenement.target.value);
              setLignes([]); // Le stock affiché dépend de la boutique.
            }}
          >
            {boutiques.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} — {b.nom}
              </option>
            ))}
          </select>
        </div>
        <div className="champ">
          <label htmlFor="reception-fournisseur">Fournisseur</label>
          <input
            id="reception-fournisseur"
            maxLength={100}
            autoComplete="off"
            value={fournisseur}
            onChange={(evenement) => {
              setFournisseur(evenement.target.value);
              setSuggestionsMasquees(false);
            }}
          />
          {(fournisseursTrouves ?? []).length > 0 && (
            <ul className="liste-choix" aria-label="Fournisseurs déjà enregistrés">
              {(fournisseursTrouves ?? []).map((suggestion) => (
                <li key={suggestion.id}>
                  <button
                    type="button"
                    className="bouton--discret"
                    onClick={() => {
                      setFournisseur(suggestion.nom);
                      setSuggestionsMasquees(true);
                    }}
                  >
                    {suggestion.nom}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="champ">
          <label htmlFor="reception-reference">N° de bon ou de facture (facultatif)</label>
          <input
            id="reception-reference"
            maxLength={60}
            value={reference}
            onChange={(evenement) => {
              setReference(evenement.target.value);
            }}
          />
        </div>
        <div className="champ">
          <label htmlFor="reception-date">Date de réception</label>
          <input
            id="reception-date"
            type="date"
            max={aujourdhui()}
            value={date}
            onChange={(evenement) => {
              setDate(evenement.target.value);
            }}
          />
        </div>
        {avecCout && (
          <div className="champ">
            <label htmlFor="reception-frais">
              Frais de transport et de manutention (FCFA, facultatif)
            </label>
            <input
              id="reception-frais"
              inputMode="numeric"
              value={frais}
              onChange={(evenement) => {
                setFrais(evenement.target.value.replace(/\D/g, ''));
              }}
            />
          </div>
        )}
      </div>

      <LignesArticles
        boutiqueId={boutique === '' ? null : boutique}
        lignes={lignes}
        onChange={setLignes}
        avecCout={avecCout}
      />

      <Erreur erreur={envoi.error} />
      <div className="ligne" style={{ marginTop: 12 }}>
        <button
          type="button"
          disabled={!complet || envoi.isPending}
          onClick={() => {
            envoi.mutate();
          }}
        >
          {envoi.isPending ? 'Enregistrement…' : 'Enregistrer l’entrée en stock'}
        </button>
      </div>
    </Carte>
  );
}
